import React, { useCallback, useEffect, useRef, useState } from 'react'

export type ConfirmTone = 'default' | 'danger'

export interface ConfirmOptions {
  /**
   * Message body rendered inside the dialog, associated via aria-labelledby.
   * Accepts a plain string or a `React.ReactNode` (e.g. JSX with `<strong>`
   * for interpolated emphasis) — widened in U15 to support consumers that
   * need to highlight interpolated values without a custom overlay.
   */
  message: React.ReactNode
  /** Visual/semantic emphasis for the confirm action. Defaults to 'default'. */
  tone?: ConfirmTone
  /** Label for the confirm button. Defaults to 'Confirm' when omitted. */
  confirmLabel?: string
  /** Label for the cancel button. Defaults to 'Cancel' when omitted. */
  cancelLabel?: string
}

export interface UseBlockingConfirmResult {
  /**
   * Opens the blocking dialog and resolves with `true` (confirm) or `false`
   * (cancel, ESC, backdrop click, or host unmount). Rejects if another
   * `ask()` is already pending — callers must await/settle one at a time.
   */
  ask: (options: ConfirmOptions) => Promise<boolean>
  /** Renders the dialog (or `null` when idle). Call once near the JSX root. */
  Confirm: () => React.ReactElement | null
}

interface PendingConfirm {
  message: React.ReactNode
  tone: ConfirmTone
  confirmLabel: string
  cancelLabel: string
  resolve: (value: boolean) => void
}

const DIALOG_MESSAGE_ID = 'blocking-confirm-message'

/**
 * Promise-based blocking confirm primitive: `{ ask, Confirm }`.
 *
 * No context/provider, no render-props, no separate importable view
 * component, no `ReactDOM.createPortal`. The host component calls
 * `{confirmModal.Confirm()}` once near the JSX root and awaits `ask()`
 * at each call site that previously used `window.confirm()`.
 */
export function useBlockingConfirm(): UseBlockingConfirmResult {
  const [pending, setPending] = useState<PendingConfirm | null>(null)
  const pendingRef = useRef<PendingConfirm | null>(null)
  const confirmButtonRef = useRef<HTMLButtonElement | null>(null)
  const cancelButtonRef = useRef<HTMLButtonElement | null>(null)

  const settle = useCallback((value: boolean) => {
    const current = pendingRef.current
    if (!current) return
    pendingRef.current = null
    setPending(null)
    current.resolve(value)
  }, [])

  const ask = useCallback((options: ConfirmOptions): Promise<boolean> => {
    if (pendingRef.current) {
      return Promise.reject(
        new Error('BlockingConfirm: overlapping ask; another modal is already open')
      )
    }
    return new Promise<boolean>((resolve) => {
      const next: PendingConfirm = {
        message: options.message,
        tone: options.tone ?? 'default',
        confirmLabel: options.confirmLabel ?? 'Confirm',
        cancelLabel: options.cancelLabel ?? 'Cancel',
        resolve
      }
      pendingRef.current = next
      setPending(next)
    })
  }, [])

  // Unmount safety: resolve any still-pending ask with false instead of
  // leaving the caller's promise hanging forever.
  useEffect(() => {
    return () => {
      const current = pendingRef.current
      if (current) {
        pendingRef.current = null
        current.resolve(false)
      }
    }
  }, [])

  // Default focus lands on the Cancel button whenever the dialog opens.
  useEffect(() => {
    if (pending) {
      cancelButtonRef.current?.focus()
    }
  }, [pending])

  // ESC dismisses with false. Basic focus trap: Tab cycles Confirm <-> Cancel.
  useEffect(() => {
    if (!pending) return undefined

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        settle(false)
        return
      }
      if (event.key === 'Tab') {
        event.preventDefault()
        const focusCancel = document.activeElement !== cancelButtonRef.current
        if (focusCancel) cancelButtonRef.current?.focus()
        else confirmButtonRef.current?.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown, true)
    return () => document.removeEventListener('keydown', onKeyDown, true)
  }, [pending, settle])

  const onBackdropMouseDown = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (event.target === event.currentTarget) settle(false)
    },
    [settle]
  )

  const Confirm = useCallback((): React.ReactElement | null => {
    if (!pending) return null

    return React.createElement(
      'div',
      {
        'data-testid': 'blocking-confirm-backdrop',
        onMouseDown: onBackdropMouseDown,
        style: {
          position: 'fixed',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'rgba(0, 0, 0, 0.45)',
          zIndex: 1000
        }
      },
      React.createElement(
        'div',
        {
          role: 'alertdialog',
          'aria-modal': 'true',
          'aria-labelledby': DIALOG_MESSAGE_ID,
          style: {
            background: '#fff',
            borderRadius: 8,
            padding: 24,
            maxWidth: 420,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.25)'
          }
        },
        React.createElement(
          'p',
          { id: DIALOG_MESSAGE_ID, style: { whiteSpace: 'pre-line' } },
          pending.message
        ),
        React.createElement(
          'div',
          { style: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 } },
          React.createElement(
            'button',
            {
              type: 'button',
              ref: cancelButtonRef,
              onClick: () => settle(false)
            },
            pending.cancelLabel
          ),
          React.createElement(
            'button',
            {
              type: 'button',
              ref: confirmButtonRef,
              'data-tone': pending.tone,
              onClick: () => settle(true)
            },
            pending.confirmLabel
          )
        )
      )
    )
  }, [pending, onBackdropMouseDown, settle])

  return { ask, Confirm }
}
